import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, apiJson, ApiError } from '../api/client';
import { captureGeoIfNeeded } from './geoCapture';

export type UserRole = 'admin' | 'user';

export interface SessionUser {
  email: string;
  name: string | null;
  role: UserRole;
}

export interface SessionInfo {
  authenticated: boolean;
  kind: 'session' | 'service-token' | null;
  user: SessionUser | null;
}

interface AuthContextValue {
  loading: boolean;
  session: SessionInfo;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const defaultSession: SessionInfo = { authenticated: false, kind: null, user: null };

const AuthContext = createContext<AuthContextValue>({
  loading: true,
  session: defaultSession,
  refresh: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<SessionInfo>(defaultSession);

  const refresh = useCallback(async () => {
    try {
      const me = await api<{
        authenticated: boolean;
        kind: 'session' | 'service-token';
        user: SessionUser | null;
      }>('/api/auth/me');
      setSession({ authenticated: me.authenticated, kind: me.kind, user: me.user });
      // Opportunistic browser-geo capture for the just-authenticated user.
      // Fires once per session per browser, with consent cached in
      // localStorage so denied users never see a second prompt.
      if (me.authenticated && me.kind === 'session') {
        void captureGeoIfNeeded();
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setSession(defaultSession);
      } else {
        setSession(defaultSession);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await apiJson('/api/auth/logout', 'POST');
    } catch {
      // ignore — cookie is gone either way
    }
    setSession(defaultSession);
  }, []);

  const value = useMemo(
    () => ({ loading, session, refresh, logout }),
    [loading, session, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
